import { call, fork, put, select, takeEvery } from 'redux-saga/effects';
import type { Action } from 'redux-actions';
import type { ProcessDialogCommand, ProcessResultDto, ScenarioDto } from '@/api-gen/api.schemas';
import type { ProcessResultDto as LegacyProcessResultDto } from '@/api-gen/api.schemas';
import { processProcessDialog } from '@/api-gen/api.ts';
import type { IDProcessState } from 'app/models/dialogs/dialogProcess.ts';
import * as ScenarioActions from 'app/actions/scenarios/scenarioAction.ts';
import { DialogProcessorActions } from 'app/actions/dialogprocessor/dialogProcessorActions.ts';

import type { AxiosResponse } from 'axios';
import * as DialogProcessorSelector from 'app/selectors/dialogProcessorSelector.ts';
import * as ScenarioChartSelectors from 'app/selectors/scenarioChartSelectors.ts';
import * as ScenarioSelectors from 'app/selectors/scenarioSelectors.ts';
import type { CustomChart } from 'app/models/scenarios/customChart.ts';
import type { LinkedScenario } from 'app/models/intents/intentNodeProperties.ts';
import { VoiceRecognitionActions } from 'app/actions/voiceRecognition/voiceRecognitionActions.ts';
import logger from 'app/utils/logger.ts';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@tanstack/react-query';

function* getDPResponse(params: Action<{ text: string; speechBase64?: string; start?: string; end?: string }>) {
  const currentChart: CustomChart = yield select(ScenarioChartSelectors.scenarioChart);
  const scenarioLinks: LinkedScenario[] = yield select(ScenarioSelectors.linkedScenarios);
  const currentChartScenario: ScenarioDto = yield select(ScenarioSelectors.currentChartScenario);
  const currentMessage = params.payload.text;
  const useStreaming: boolean = yield select(ScenarioSelectors.isStreamingEnabledForScenario);
  const lastResponse: IDProcessState = yield select(DialogProcessorSelector.dialogProcessorSelector);

  if (!lastResponse.sessionId) {
    logger.warn('Skipping getDPResponse - no sessionId available');
    return;
  }

  const lastBlock = !!lastResponse.blocks.currentBlock ? lastResponse.blocks.currentBlock : null;
  const intentProcessingProvider = (currentChartScenario as any).intentProcessingProvider;

  try {
    const payload: ProcessDialogCommand = {
      sessionId: lastResponse.sessionId,
      text: currentMessage,
      speech: params.payload.speechBase64
    };

    const response: AxiosResponse<ProcessResultDto> = yield call(processProcessDialog, payload);

    if (!useStreaming) {
      // Cast to legacy type to keep reducer/action typings stable during migration
      yield put(
        DialogProcessorActions.setResponseDataStore(response.data as unknown as LegacyProcessResultDto)
      );
    }

    if (response.data.lastBlock && response.data.lastBlock.sceneId !== currentChart.properties.id) {
      yield put(ScenarioActions.toNextLinkedScenario(true));
      yield put(
        ScenarioActions.switchCurrentScenarioTo(
          scenarioLinks.findIndex((x) => x.sceneId === response.data.lastBlock.sceneId)
        )
      );
    }
  } catch (error) {
    logger.log('Error getDPResponse: ', error);
  } finally {
    yield put(VoiceRecognitionActions.setUseFallbackStt(false));
  }
}

// Watcher saga to monitor the GET_RESPONSE_DATA action
function* watchGetDPResponse() {
  yield takeEvery(DialogProcessorActions.Type.GET_RESPONSE_DATA, getDPResponse);
}

// Root saga for dialog processor-related sagas
export function* watchAllDPResponseSaga() {
  yield fork(watchGetDPResponse);
}

export const useDialogProcessorMutation = () => {
  const dispatch = useDispatch();
  const currentChart: CustomChart = useSelector(ScenarioChartSelectors.scenarioChart);
  const scenarioLinks: LinkedScenario[] = useSelector(ScenarioSelectors.linkedScenarios);
  const currentChartScenario: ScenarioDto = useSelector(ScenarioSelectors.currentChartScenario);
  const lastResponse: IDProcessState = useSelector(DialogProcessorSelector.dialogProcessorSelector);
  const useStreaming = useSelector(ScenarioSelectors.isStreamingEnabledForScenario);

  const getDPResponseMutation = useMutation({
    mutationFn: async (params: { text: string; speechBase64?: string; start?: string; end?: string }) => {
      if (!lastResponse.sessionId) {
        logger.warn('Skipping getDPResponse - no sessionId available', {
          scenarioId: currentChart.properties.id,
          hasCurrentScenario: !!currentChartScenario,
          state: 'mutation'
        });
        // Return a mock response to keep the app logic consistent
        return { data: null };
      }

      logger.info('Processing dialog with session', {
        sessionId: lastResponse.sessionId,
        scenarioId: currentChart.properties.id
      });
      const currentMessage = params.text;
      const lastBlock = !!lastResponse.blocks.currentBlock ? lastResponse.blocks.currentBlock : null;
      const intentProcessingProvider = (currentChartScenario as any).intentProcessingProvider;

      const payload: ProcessDialogCommand = {
        sessionId: lastResponse.sessionId,
        text: currentMessage,
        speech: params.speechBase64
      };

      return processProcessDialog(payload);
    },
    onSuccess: (response) => {
      if (!useStreaming) {
        // Cast to legacy type to keep reducer/action typings stable during migration
        dispatch(
          DialogProcessorActions.setResponseDataStore(
            response.data as unknown as LegacyProcessResultDto
          )
        );
      }

      if (response.data.lastBlock && response.data.lastBlock.sceneId !== currentChart.properties.id) {
        dispatch(ScenarioActions.toNextLinkedScenario(true));
        dispatch(
          ScenarioActions.switchCurrentScenarioTo(
            scenarioLinks.findIndex((x) => x.sceneId === response.data.lastBlock.sceneId)
          )
        );
      }
    },
    onError: (error) => {
      logger.log('Error getDPResponse: ', error);
    },
    onSettled: () => {
      logger.log('onSettled in useMutation');
      dispatch(VoiceRecognitionActions.setUseFallbackStt(false));
    }
  });

  return getDPResponseMutation;
};
